import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React from 'react';
import styled from 'styled-components';
import { useCommandUploadFileMutation } from '../../app/api';
import { type Job, type JobStepStatus, type ResponseGetJob, useAddAttachments, useUpdateJob } from '../../app/ApiGen';
import { JobStatus } from '../../app/enums';
import { Card, Section } from '../../design-system';
import { standardiseError } from '../../utils/error';
import { mapCertificateOfComplianceUpdateToRequestUpdateJob } from '../../utils/jobs/mappers';
import type { Normalised } from '../../utils/request';

interface UploadCoCSectionProps {
  job: Normalised<Job>;
  refetchJob: () => Promise<ResponseGetJob | null>;
}

interface JobProgress {
  commissioning: JobStepStatus;
  postWorkForm: JobStepStatus;
  preWorkForm: JobStepStatus;
  certificateOfCompliance?: JobStepStatus;
}

const VisuallyHiddenInput = styled('input')<React.HTMLProps<HTMLInputElement>>`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const isLegacyJob = (job: Normalised<Job>): boolean => !job.details?.progress;

const canUploadCertificate = (job: Normalised<Job>): boolean => {
  if (isLegacyJob(job)) return false;

  const isAGLJob = Boolean(job.details.isAGLJob);
  const progress = job.details.progress as JobProgress;

  const postWorkCompleted = progress.postWorkForm === 'completed' || progress.postWorkForm === 'manually completed';
  const commissionStepCompleted =
    progress.commissioning === 'completed' ||
    progress.commissioning === 'manually completed' ||
    progress.commissioning === 'failed';
  const standardUploadAllowed = (postWorkCompleted && isAGLJob) || (commissionStepCompleted && !isAGLJob);

  return standardUploadAllowed;
};

const extractFileExtension = (filename: string): string => {
  const extension = filename.split('.').pop();
  if (!extension) {
    throw new Error('File must have an extension');
  }
  return extension;
};

const LegacyJobAlert: React.FC = () => (
  <Section>
    <Card title="Upload Certificate of Compliance">
      <Box display="flex" flexDirection="column" p={2} gap={1}>
        <Alert severity="info">
          Job does not support Certificate of Compliance (CoC) upload, please contact{' '}
          <a href="mailto:support@evnex.com" style={{ color: '#0056b3', textDecoration: 'none' }}>
            support@evnex.com
          </a>{' '}
          or the installation team with the job details and provide the correct CoC{' '}
        </Alert>
      </Box>
    </Card>
  </Section>
);

const PreventUploadAlert: React.FC = () => (
  <Section>
    <Card title="Upload Certificate of Compliance">
      <Box display="flex" flexDirection="column" p={2} gap={1}>
        <Alert severity="info">
          Please contact{' '}
          <a href="mailto:support@evnex.com" style={{ color: '#0056b3', textDecoration: 'none' }}>
            support@evnex.com
          </a>{' '}
          with the job details and provide the correct Certificate of Compliance
        </Alert>
      </Box>
    </Card>
  </Section>
);

const PrerequisiteNotMetAlert: React.FC = () => (
  <Section>
    <Card title="Upload Certificate of Compliance">
      <Box display="flex" flexDirection="column" p={2} gap={1}>
        <Alert severity="warning">Cannot upload Certificate of Compliance until form steps are completed.</Alert>
      </Box>
    </Card>
  </Section>
);

interface FileUploadButtonProps {
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const FileUploadButton: React.FC<FileUploadButtonProps> = ({ isUploading, fileInputRef, onFileChange }) => (
  <Button
    component="label"
    variant="contained"
    color="primary"
    startIcon={<CloudUploadIcon />}
    disabled={isUploading}
    sx={{
      textTransform: 'none',
      padding: '10px 20px',
      fontSize: '16px',
      '&:hover': {
        backgroundColor: '#0056b3',
      },
    }}
  >
    {isUploading ? 'Uploading...' : 'Upload File'}
    <VisuallyHiddenInput ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onFileChange} />
  </Button>
);

const UploadingIndicator: React.FC = () => (
  <Box display="flex" alignItems="center" gap={1}>
    <CircularProgress size={20} />
    <Typography variant="body2" color="textSecondary">
      Uploading your file, please wait...
    </Typography>
  </Box>
);

const UploadSection: React.FC<FileUploadButtonProps> = (props) => {
  const { isUploading } = props;
  return (
    <Section>
      <Card title="Upload Certificate of Compliance">
        <Box display="flex" flexDirection="column" p={2} gap={1}>
          <Typography variant="body1" color="textSecondary">
            Please upload a Certificate of Compliance file (PDF, JPG, PNG):
          </Typography>
          <FileUploadButton {...props} />
          {isUploading && <UploadingIndicator />}
        </Box>
      </Card>
    </Section>
  );
};

export const UploadCoCSection: React.FC<UploadCoCSectionProps> = ({ job, refetchJob }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const { mutate: addAttachments } = useAddAttachments({ jobId: job.id });
  const { mutate: updateJob } = useUpdateJob({ jobId: job.id });

  const [uploadFile] = useCommandUploadFileMutation();

  const uploadAttachment = React.useCallback(
    async (file: File): Promise<{ fileName: string; signedUrl: string }> => {
      const fileName = `certificateOfCompliance.${extractFileExtension(file.name)}`;
      const attachmentsResponse = await addAttachments({ data: [{ attributes: { fileName }, type: 'attachments' }] });
      await uploadFile({ jobId: job.id, file, fileName }).unwrap();
      return { fileName, signedUrl: attachmentsResponse.data[0].id };
    },
    [addAttachments, job.id, uploadFile],
  );

  const handleFileUpload = React.useCallback(
    async (file: File): Promise<void> => {
      try {
        setIsUploading(true);
        const attachment = await uploadAttachment(file);
        const updateJobRequest = mapCertificateOfComplianceUpdateToRequestUpdateJob({
          attachment,
          job: { id: job.id, progress: job.details.progress },
        });
        await updateJob(updateJobRequest);
        await refetchJob();
        enqueueSnackbar('File uploaded successfully', { variant: 'success' });
      } catch (e) {
        const error = standardiseError(e);
        enqueueSnackbar(`Unable to upload file: ${error.message}`, { variant: 'error' });
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [uploadAttachment, job.id, job.details.progress, updateJob, refetchJob, enqueueSnackbar],
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file: File | undefined = event.target.files?.[0];

    if (!file) {
      enqueueSnackbar('Please select a file to upload', { variant: 'warning' });
      return;
    }

    try {
      await handleFileUpload(file);
    } catch (error) {
      console.error('Error handling file upload:', error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (
    job.status === JobStatus.ManuallyCommissioned ||
    job.status === JobStatus.Cancelled ||
    job.status === JobStatus.Error
  ) {
    return <PreventUploadAlert />;
  }

  if (job.status === JobStatus.Pending) {
    return <PrerequisiteNotMetAlert />;
  }

  if (isLegacyJob(job)) {
    return <LegacyJobAlert />;
  }

  const canUpload = canUploadCertificate(job);
  if (!canUpload) {
    return <PrerequisiteNotMetAlert />;
  }

  return (
    <UploadSection
      isUploading={isUploading}
      fileInputRef={fileInputRef}
      onFileChange={(event) => {
        handleFileChange(event).catch(console.error);
      }}
    />
  );
};
